import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, UtensilsCrossed, Calendar, Truck, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-nanny-cream via-white to-nanny-beige py-24 md:py-40 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-nanny-maroon/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-nanny-accent/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            {/* Main Headline - Make it POP */}
            <div className="mb-8 relative">
              <h1 className="text-6xl md:text-8xl font-bold text-nanny-dark mb-4 heading-font leading-tight">
                <span className="relative inline-block">
                  <span className="relative z-10">Homemade</span>
                  <span className="absolute bottom-2 left-0 w-full h-4 bg-nanny-accent/30 -rotate-1"></span>
                </span>
                <br />
                <span className="text-nanny-maroon">Goodness,</span>
                <br />
                <span className="relative inline-block">
                  Every Single Day
                  <span className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-nanny-maroon rounded-full"></span>
                </span>
              </h1>
            </div>
            
            <p className="text-xl md:text-2xl text-gray-700 mb-10 max-w-2xl mx-auto font-medium">
              From our kitchen to yours since 2021. Authentic home-cooked meals and catering services in Huntingdon, TN.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/menu" className="w-full sm:w-auto">
                <Button size="lg" className="bg-nanny-maroon hover:bg-nanny-dark text-white text-lg px-10 py-7 w-full shadow-lg hover:shadow-xl transition-all">
                  View Our Menu
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/order" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="border-2 border-nanny-maroon text-nanny-maroon hover:bg-nanny-maroon hover:text-white text-lg px-10 py-7 w-full shadow-lg hover:shadow-xl transition-all">
                  Order Catering
                </Button>
              </Link>
            </div>
            
            <div className="flex items-center justify-center space-x-2 text-nanny-dark">
              <Star className="h-5 w-5 fill-nanny-accent text-nanny-accent" />
              <Star className="h-5 w-5 fill-nanny-accent text-nanny-accent" />
              <Star className="h-5 w-5 fill-nanny-accent text-nanny-accent" />
              <Star className="h-5 w-5 fill-nanny-accent text-nanny-accent" />
              <Star className="h-5 w-5 fill-nanny-accent text-nanny-accent" />
              <span className="ml-2 text-sm font-semibold">Loved by our community</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nanny-dark mb-4 heading-font">What We Offer</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Quality homemade meals for every occasion</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-100 hover:border-nanny-maroon hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="bg-nanny-cream rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <UtensilsCrossed className="h-10 w-10 text-nanny-maroon" />
                </div>
                <h3 className="text-2xl font-bold text-nanny-dark mb-4">Take-Home Meals</h3>
                <p className="text-gray-600 mb-6 min-h-[72px]">
                  Fresh, homemade meals ready to take home. Our menu changes weekly - check Facebook for this week's delicious options!
                </p>
                <Link to="/menu">
                  <Button variant="link" className="text-nanny-maroon h-10 inline-flex items-center">
                    See This Week's Menu <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-nanny-maroon hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="bg-nanny-cream rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Calendar className="h-10 w-10 text-nanny-maroon" />
                </div>
                <h3 className="text-2xl font-bold text-nanny-dark mb-4">Catering Services</h3>
                <p className="text-gray-600 mb-6 min-h-[72px]">
                  Full catering for breakfast, lunch, and supper. Perfect for parties, events, and gatherings of any size.
                </p>
                <Link to="/catering">
                  <Button variant="link" className="text-nanny-maroon h-10 inline-flex items-center">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-nanny-maroon hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="bg-nanny-cream rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Truck className="h-10 w-10 text-nanny-maroon" />
                </div>
                <h3 className="text-2xl font-bold text-nanny-dark mb-4">Party Trays</h3>
                <p className="text-gray-600 mb-6 min-h-[72px]">
                  Delicious party trays, dips, desserts, and more for your special occasions and gatherings.
                </p>
                <Link to="/order">
                  <Button variant="link" className="text-nanny-maroon h-10 inline-flex items-center">
                    Order Now <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Images Section */}
      <section className="py-20 bg-nanny-cream">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nanny-dark mb-4 heading-font">Made Fresh Daily</h2>
            <p className="text-xl text-gray-600">Every dish prepared with love and care</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/997cyzj8_473547096_928662969397727_4010279095934232412_n.jpg" 
                alt="Dessert Tray" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Sweet Treats</h3>
                  <p className="text-sm">Homemade desserts & cookies</p>
                </div>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/yhtx9eg6_476402542_946735560923801_5528077024555438637_n.jpg" 
                alt="Party Sliders" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Party Trays</h3>
                  <p className="text-sm">Perfect for any gathering</p>
                </div>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/sbwfqi03_472970184_926496479614376_2342581467839661898_n.jpg" 
                alt="Fresh Wraps" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Fresh & Healthy</h3>
                  <p className="text-sm">Wraps and salads</p>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link to="/gallery">
              <Button size="lg" variant="outline" className="border-2 border-nanny-maroon text-nanny-maroon hover:bg-nanny-maroon hover:text-white">
                View Full Gallery
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Location Map Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-nanny-dark mb-4 heading-font">Visit Us</h2>
              <p className="text-xl text-gray-600">Come see us in Huntingdon, TN</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Map */}
              <div className="rounded-xl overflow-hidden shadow-2xl h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.1234567890123!2d-88.42789!3d36.00123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDAwJzA0LjQiTiA4OMKwMjUnNDAuNCJX!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus&q=18725+W+Main+St,+Huntingdon,+TN+38344"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Nanny's Location"
                ></iframe>
              </div>
              
              {/* Location Details */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-nanny-dark mb-4">Nanny's</h3>
                  <div className="space-y-3 text-gray-700">
                    <p className="text-lg">
                      <span className="font-semibold">Address:</span><br />
                      18725 W Main St<br />
                      Huntingdon, TN 38344
                    </p>
                    <p className="text-lg">
                      <span className="font-semibold">Phone:</span><br />
                      <a href="tel:731-267-9884" className="text-nanny-maroon hover:text-nanny-dark transition-colors">
                        731-267-9884
                      </a>
                    </p>
                    <div>
                      <p className="font-semibold text-lg mb-2">Hours:</p>
                      <div className="space-y-1">
                        <p><span className="font-medium">In-Store:</span> Monday - Thursday, 10:30 AM - 5:30 PM</p>
                        <p><span className="font-medium">Catering:</span> 7 Days a Week</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <a 
                    href="https://www.google.com/maps/dir/?api=1&destination=18725+W+Main+St,+Huntingdon,+TN+38344"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-nanny-maroon hover:bg-nanny-dark text-white">
                      Get Directions
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-nanny-maroon text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="wheat" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M50 10 Q55 30 50 50 Q45 30 50 10" stroke="white" fill="none" strokeWidth="2"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#wheat)" />
          </svg>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 heading-font">Ready to Order?</h2>
          <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto text-nanny-cream">
            Whether you need a quick take-home meal or catering for your next event, we're here to serve you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:731-267-9884">
              <Button size="lg" className="bg-white text-nanny-maroon hover:bg-nanny-cream text-lg px-8 py-6 w-full sm:w-auto">
                Call 731-267-9884
              </Button>
            </a>
            <Link to="/order">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-nanny-maroon text-lg px-8 py-6 w-full sm:w-auto">
                Order Online
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
